import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewChecked, inject, ChangeDetectionStrategy, signal, forwardRef } from '@angular/core';

import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { StepType } from '../../shared/interfaces/step-type.interface';
import { CodeEditorComponent } from '../../shared/modules/code-editor/code-editor.component';
import { Subscription } from 'rxjs';
import { LlmService } from '../../shared/services/llm/llm.service';
import {
  LlmStreamEvent,
  LlmUsage,
} from '../../shared/interfaces/llm.interface';

interface StepFormData {
  title: string;
  stepTypes: StepType[];
  step: {
    stepId?: number;
    name: string;
    steptypeId: number;
    content: string;
    description?: string;
  };
  // AI generation context (only present for new steps)
  projectId?: number;
  projectName?: string;
  workflowId?: number;
  steps?: { name: string; steptypeId: number }[];
  llmAvailable?: boolean;
}

/**
 * Editor mode configuration
 */
const EDITOR_MODE_CONFIG = {
  DEFAULT: 'text',
  PYTHON: 'python',
  JAVASCRIPT: 'javascript',
  MARKDOWN: 'markdown',
  DEBUG: 'debug',
} as const;

/**
 * Type name to editor mode mappings
 */
const TYPE_NAME_MAPPINGS: { keywords: string[]; mode: string }[] = [
  { keywords: ['python', 'py'], mode: EDITOR_MODE_CONFIG.PYTHON },
  {
    keywords: ['javascript', 'js', 'node'],
    mode: EDITOR_MODE_CONFIG.JAVASCRIPT,
  },
  { keywords: ['markdown', 'md', 'text'], mode: EDITOR_MODE_CONFIG.MARKDOWN },
  { keywords: ['debug', 'log'], mode: EDITOR_MODE_CONFIG.DEBUG },
];

/**
 * Step type to Material icon mapping (by keywords in type name)
 */
const STEP_TYPE_ICON_MAPPINGS: { keywords: string[]; icon: string }[] = [
  { keywords: ['python', 'py'], icon: 'code' },
  { keywords: ['javascript', 'js', 'node'], icon: 'javascript' },
  { keywords: ['markdown', 'md'], icon: 'text_fields' },
  { keywords: ['debug', 'log'], icon: 'bug_report' },
];

@Component({
  selector: 'app-step-form-dialog',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    // forwardRef breaks an NG0919 circular import: opened via FeatureDialogService
    // (imported by DialogService), and CodeEditorComponent injects DialogService
    // (SCRUM-718, follow-up to SCRUM-689).
    forwardRef(() => CodeEditorComponent),
  ],
  templateUrl: './step-form-dialog.component.html',
  styleUrls: ['./step-form-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepFormDialogComponent
  implements OnInit, OnDestroy, AfterViewChecked
{
  private fb = inject(FormBuilder);
  dialogRef = inject<MatDialogRef<StepFormDialogComponent>>(MatDialogRef);
  data = inject<StepFormData>(MAT_DIALOG_DATA);
  private readonly llmService = inject(LlmService);

  stepForm: FormGroup;
  currentEditorMode = signal('text');
  private subscription = new Subscription();

  @ViewChild('codePreview') codePreview?: ElementRef<HTMLPreElement>;
  private shouldScrollPreview = false;

  // AI generation state
  aiEnabled = false;
  aiInstructions = '';
  generatedCode = signal('');
  isGenerating = signal(false);
  generationError = signal('');
  generationMeta = signal<{ model?: string; usage?: LlmUsage } | null>(null);
  private abortGeneration: (() => void) | null = null;

  // Support both new step types and legacy chunk types
  get stepTypes(): StepType[] {
    return this.data.stepTypes || [];
  }

  // Check if this is a new step creation (vs editing existing step)
  get isCreatingNewStep(): boolean {
    return !this.data.step.stepId || this.data.title === 'Add New Step';
  }

  get llmAvailable(): boolean {
    return !!this.data.llmAvailable;
  }

  get showAiSection(): boolean {
    return this.isCreatingNewStep && this.llmAvailable;
  }

  get priorSteps(): { name: string; steptypeId: number }[] {
    return this.data.steps || [];
  }

  get priorStepNames(): string {
    const steps = this.priorSteps;
    return `${steps.length} (${steps.map((s) => s.name).join(', ')})`;
  }

  constructor() {
    const data = this.data;

    // Support both new and legacy field names
    const typeId =
      data.step.steptypeId ||
      (data.step as any).steptypeId ||
      (this.stepTypes.length > 0 ? this.stepTypes[0].steptypeId : 0);

    this.stepForm = this.fb.group({
      name: [data.step.name, Validators.required],
      steptypeId: [typeId, Validators.required],
      content: [data.step.content || ''],
      description: [data.step.description || ''],
    });
  }

  ngAfterViewChecked(): void {
    if (this.shouldScrollPreview && this.codePreview) {
      const el = this.codePreview.nativeElement;
      el.scrollTop = el.scrollHeight;
      this.shouldScrollPreview = false;
    }
  }

  ngOnInit(): void {
    this.currentEditorMode.set(this.getEditorMode());
    this.subscribeToStepTypeChanges();
  }

  /**
   * Subscribe to step type changes to update editor mode
   */
  private subscribeToStepTypeChanges(): void {
    const stepTypeControl = this.stepForm.get('steptypeId');
    if (stepTypeControl) {
      this.subscription.add(
        stepTypeControl.valueChanges.subscribe(() => {
          this.currentEditorMode.set(this.getEditorMode());
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.abortGeneration?.();
    this.subscription.unsubscribe();
  }

  /**
   * Get the code editor mode based on the selected step type
   */
  getEditorMode(): string {
    const selectedStepTypeId = this.getSelectedStepTypeId();
    const typeName = this.getStepTypeName(selectedStepTypeId);

    if (!typeName) {
      return EDITOR_MODE_CONFIG.DEFAULT;
    }

    return this.mapTypeNameToEditorMode(typeName);
  }

  /**
   * Get the selected step type ID from the form
   */
  private getSelectedStepTypeId(): number | null {
    return this.stepForm.get('steptypeId')?.value || null;
  }

  /**
   * Get the step type name from the selected ID
   */
  private getStepTypeName(stepTypeId: number | null): string {
    if (!stepTypeId || this.stepTypes.length === 0) {
      return '';
    }

    const stepType = this.stepTypes.find(
      (type) => type.steptypeId === stepTypeId,
    );
    return stepType?.name?.toLowerCase() || '';
  }

  /**
   * Map type name to editor mode
   */
  private mapTypeNameToEditorMode(typeName: string): string {
    for (const mapping of TYPE_NAME_MAPPINGS) {
      if (mapping.keywords.some((keyword) => typeName.includes(keyword))) {
        return mapping.mode;
      }
    }

    return EDITOR_MODE_CONFIG.DEFAULT;
  }

  /**
   * Handle content updates from the code editor
   */
  onContentUpdate(content: string): void {
    this.stepForm.get('content')?.setValue(content);
  }

  /**
   * Mark form as touched when editing starts
   */
  setFormTouched(): void {
    this.stepForm.markAsTouched();
  }

  onCancel(): void {
    this.abortGeneration?.();
    this.dialogRef.close();
  }

  onSubmit(): void {
    if (this.stepForm.valid) {
      const result = { ...this.stepForm.value };
      if (this.aiEnabled && this.generatedCode()) {
        result.content = this.stripCodeFences(this.generatedCode());
      }
      this.dialogRef.close(result);
    }
  }

  getTypeDescription(typeId: number): string {
    // Get step type description
    if (this.stepTypes.length > 0) {
      const stepType = this.stepTypes.find(
        (type) => type.steptypeId === typeId,
      );
      return stepType ? stepType.description : '';
    }
    return '';
  }

  /**
   * Legacy compatibility method
   * @deprecated Use getTypeDescription() instead. This method exists for backward compatibility
   * with older code that may still reference it. Can be removed once all references are updated.
   */
  getTypeDescription_legacy(steptypeId: number): string {
    return this.getTypeDescription(steptypeId);
  }

  // Template helper methods to handle union types safely
  getAvailableTypes(): StepType[] {
    return this.stepTypes;
  }

  getTypeId(type: StepType): number {
    return type.steptypeId;
  }

  /**
   * Get the Material icon name for a step type
   */
  getStepTypeIcon(type: StepType): string {
    const typeName = (type.name || '').toLowerCase();
    for (const mapping of STEP_TYPE_ICON_MAPPINGS) {
      if (mapping.keywords.some((keyword) => typeName.includes(keyword))) {
        return mapping.icon;
      }
    }
    return 'code';
  }

  /**
   * Get the selected step type for displaying icon in the select trigger
   */
  getSelectedStepType(): StepType | undefined {
    const id = this.stepForm.get('steptypeId')?.value;
    return id != null
      ? this.stepTypes.find((t) => t.steptypeId === id)
      : undefined;
  }

  // ── AI Generation Methods ────────────────────────────────────────────

  toggleAi(): void {
    this.aiEnabled = !this.aiEnabled;
    if (!this.aiEnabled) {
      this.resetAiState();
    }
  }

  generateCode(): void {
    if (!this.data.projectId || !this.data.workflowId) return;

    const description = this.stepForm.get('description')?.value || '';
    const name = this.stepForm.get('name')?.value || '';
    const stepTypeName = this.getSelectedStepTypeName();

    this.isGenerating.set(true);
    this.generatedCode.set('');
    this.generationError.set('');
    this.generationMeta.set(null);

    const { events$, abort } = this.llmService.generateStep(
      this.data.projectId,
      {
        description: this.buildGenerationPrompt(name, description),
        stepType: stepTypeName,
        workflowId: this.data.workflowId,
        insertAt: this.priorSteps.length,
        stream: true,
      },
    );

    this.abortGeneration = abort;

    this.subscription.add(
      events$.subscribe({
        next: (event: LlmStreamEvent) => {
          if ('error' in event) {
            this.generationError.set(event.error);
            this.isGenerating.set(false);
            return;
          }
          this.generatedCode.update((v) => v + event.content);
          this.shouldScrollPreview = true;
          if (event.done) {
            this.isGenerating.set(false);
            this.generationMeta.set({
              model: event.model,
              usage: event.usage,
            });
            this.abortGeneration = null;
          }
        },
        error: (err: unknown) => {
          this.isGenerating.set(false);
          this.generationError.set(
            err instanceof Error ? err.message : 'Generation failed',
          );
          this.abortGeneration = null;
        },
        complete: () => {
          this.isGenerating.set(false);
          this.abortGeneration = null;
        },
      }),
    );
  }

  stopGeneration(): void {
    this.abortGeneration?.();
    this.isGenerating.set(false);
    this.abortGeneration = null;
  }

  regenerate(): void {
    this.generateCode();
  }

  private buildGenerationPrompt(name: string, description: string): string {
    const base = description.trim() || `Create a step called "${name}"`;
    if (this.aiInstructions?.trim()) {
      return `${base}\n\nAdditional instructions: ${this.aiInstructions}`;
    }
    return base;
  }

  private getSelectedStepTypeName(): string {
    const id = this.stepForm.get('steptypeId')?.value;
    const type = this.stepTypes.find((t) => t.steptypeId === id);
    return type?.name || '';
  }

  /**
   * Strip markdown code fences if the LLM wraps output in them.
   */
  private stripCodeFences(code: string): string {
    const trimmed = code.trim();
    const fenceStart = /^```[\w]*\n?/;
    const fenceEnd = /\n?```\s*$/;
    if (fenceStart.test(trimmed) && fenceEnd.test(trimmed)) {
      return trimmed.replace(fenceStart, '').replace(fenceEnd, '');
    }
    return code;
  }

  private resetAiState(): void {
    this.abortGeneration?.();
    this.generatedCode.set('');
    this.isGenerating.set(false);
    this.generationError.set('');
    this.generationMeta.set(null);
    this.aiInstructions = '';
  }
}
